import { useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Trans, useTranslation } from 'react-i18next'
import { Transition } from 'react-transition-group'

import { useAppLayout } from '@/components/AppLayout/AppLayoutProvider'
import UIIcon, { UIIconRef } from '@/components/UI/UIIcon'
import { stateValueToString, useAppState } from '@/composables/useAppState'
import { useGSAP } from '@/composables/useGSAP'
import { useRunnerState } from '@/composables/useRunnerState'
import useTracking from '@/composables/useTracking'
import { useTransComponents } from '@/composables/useTransComponents'
import { UIAudio } from '@/config/AudioConfig'
import AudioEngine from '@/webgl/audio/AudioEngine'

import Button from '../Button/Button'
import UIParticles from '../Particles/UIParticles'

export default function RunnerUIPause() {
  const { root } = useAppLayout()
  const { gsap } = useGSAP()
  const { trackImpression, trackClick } = useTracking()

  const { state, persona, scenarioUnitName, backUrl } = useAppState()
  const { gameState, actor } = useRunnerState()
  const isPaused = gameState.paused
  const isVisible = isPaused && !!root.current

  const elementRef = useRef<HTMLDivElement>(null)
  const veilRef = useRef<HTMLDivElement>(null)

  const backgroundIconRef = useRef<UIIconRef>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  const onResume = useCallback(() => {
    trackClick({
      unitName: `${stateValueToString(state.value)}_${persona}_simulation${scenarioUnitName}`,
      unitVariation: 'scenario_pause',
      unitLocation: 'tyce',
      clickType: 'button',
      buttonName: 'navigation_button',
      buttonText: 'resume',
    })
    actor.send({ type: 'RESUME' })
  }, [actor, trackClick, state, persona, scenarioUnitName])

  const onEnter = useCallback(() => {
    trackImpression({
      unitName: `${stateValueToString(state.value)}_${persona}_simulation${scenarioUnitName}`,
      unitVariation: 'scenario_pause',
      unitLocation: 'tyce',
    })
    const ctx = gsap.context(() => {
      gsap
        .timeline()
        .fromTo(
          veilRef.current,
          {
            opacity: 0,
          },
          {
            opacity: 1,
            duration: 0.5,
            ease: 'power2.out',
          },
          0
        )
        .addLabel('start', 0.3)

      ////////////////////////////////
      AudioEngine.play(UIAudio.Pop)
      const timeline = gsap.timeline()

      timeline.addLabel('start')
      timeline.fromTo(
        backgroundIconRef.current,
        {
          opacity: 0,
        },
        {
          opacity: 1,
          duration: 0.5,
          ease: 'power3.out',
        },
        'start'
      )
      timeline.fromTo(
        backgroundIconRef.current,
        {
          scale: 0,
          rotate: 90,
        },
        {
          scale: 1,
          rotate: 0,
          clearProps: 'scale,rotate',
          duration: 0.5,
          ease: 'back.out(1.7)',
        },
        'start'
      )
      timeline.fromTo(
        contentRef.current,
        {
          opacity: 0,
          // y: 64,
        },
        {
          opacity: 1,
          // y: 0,
          duration: 0.5,
          ease: 'power3.out',
        },
        'start+=0.4'
      )

      const sticker = contentRef.current.querySelector('.Sticker')
      if (sticker) {
        timeline.fromTo(
          sticker,
          {
            yPercent: -50,
            rotate: -25,
            transformOrigin: '50% 100%',
          },
          {
            yPercent: 0,
            rotate: 0,
            duration: 0.5,
            ease: 'power4.out',
          },
          'start+=0.4'
        )
      }

      const last = contentRef.current.querySelector('.Last')
      if (last) {
        timeline.fromTo(
          last,
          {
            y: 64,
          },
          {
            y: 0,
            duration: 0.5,
            ease: 'power4.out',
          },
          'start+=0.7'
        )
        timeline.fromTo(
          last,
          {
            opacity: 0,
          },
          {
            opacity: 1,
            duration: 0.5,
            ease: 'power3.out',
          },
          'start+=0.7'
        )
      }
      ////////////////////////////////
    })

    return () => ctx.kill()
  }, [gsap, veilRef, trackImpression, state, persona, scenarioUnitName])

  const onExit = useCallback(() => {
    const ctx = gsap.context(() => {
      const timeline = gsap.timeline({ delay: 0 })

      timeline.fromTo(
        veilRef.current,
        {
          opacity: 1,
        },
        {
          opacity: 0,
          duration: 0.3,
          ease: 'power2.out',
        },
        0.2
      )

      const background = elementRef.current?.querySelector('.PopinCardStep__Background')
      if (background) {
        timeline.to(
          background,
          {
            scale: 0,
            rotate: 90,
            duration: 0.5,
            ease: 'power1.out',
          },
          0
        )
      }
      const content = elementRef.current?.querySelector('.PopinCardStep__Content')
      if (content) {
        timeline.to(
          content,
          {
            opacity: 0,
            duration: 0.3,
            ease: 'power1.out',
          },
          0
        )
      }
    })

    return () => ctx.kill()
  }, [gsap, elementRef, veilRef])

  const { components } = useTransComponents()
  const { t } = useTranslation()

  return createPortal(
    <Transition
      in={isVisible}
      nodeRef={elementRef}
      onEnter={onEnter}
      onExit={onExit}
      unmountOnExit
      mountOnEnter
      timeout={{
        enter: 800,
        exit: 500,
      }}
    >
      <div
        ref={elementRef}
        className='RunnerUIPause fixed top-0 left-0 w-screen min-h-dvh md:h-screen pointer-events-none z-91 flex flex-col'
      >
        <div ref={veilRef} className='RunnerUIPauseBackground absolute top-0 left-0 w-full h-full bg-black/30' />
        <div className='w-screen py-20 my-auto'>
          <div className='relative pointer-events-none grid'>
            <div className='RunnerUIPause__Popin relative justify-self-center w-[320px] sm:w-[360px] md:w-[400px] lg:w-[max(460px,27vw)]'>
              <UIIcon
                ref={backgroundIconRef}
                icon='background3'
                className={`PopinCardStep__Background absolute w-[400px] md:w-[460px] lg:w-[520px] top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 h-auto aspect-square text-cream`}
              />
              <div
                ref={contentRef}
                className='PopinCardStep__Content relative flex flex-col items-center justify-center text-black-blue'
              >
                <h2
                  id={`dialog-title-pause`}
                  className={`text-heading-h2 mx-58 md:mx-96 text-center mt-16 mb-8 text-primary`}
                >
                  <Trans components={components}>runner.pause_screen.title</Trans>
                </h2>

                <p id={`dialog-description-pause`} className={`text-body mx-48 mt-8 text-center text-primary`}>
                  <Trans components={components}>runner.pause_screen.text</Trans>
                </p>
                <div className='flex flex-col items-stretch justify-center mt-24 gap-8'>
                  <Button
                    variant='default'
                    className={`pointer-events-auto transition-opacity duration-300 ease-out-quad`}
                    appear
                    appearDelay={0.1}
                    onClick={onResume}
                  >
                    <span className='flex items-center justify-center gap-8'>
                      <UIIcon icon='play' className='text-14' />

                      {t('runner.resume')}
                    </span>
                  </Button>
                </div>
                <UIParticles
                  className='absolute top-1/2 left-1/2 w-screen min-h-dvh md:h-screen -translate-x-1/2 -translate-y-1/2'
                  max={6}
                  exclude={{
                    colors: ['cream', 'dark-cream', 'coral', 'scarlet', 'peach'],
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>,
    root.current
  )
}
